import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { fmtNumber, currencyCode, localeDate, tz, moneyTight, fmtDateTime } from '../../utils/region';
import { useStore } from '../../store';
import InvoiceForm, { InvoicePrefill } from '../Peak/InvoiceForm';
import { brandOr } from '../../utils/brand';
import { useFirmContact } from '../../utils/firmContact';
import { SlipThumb, SlipLightbox } from '../BankStatement/SlipCell';
import { BankSlip, LeaseEvidence, LeaseEvidenceSlip } from '../../types';

// Lease Registration — READ-ONLY view of the fee assessments that live on slf-legal.co.
// Every figure (legal fee, fees/taxes, step, In-Progress/Completed) is computed on the public
// site from ONE shared module and handed over ready-made, so these numbers can never drift from
// what the public back office and the client's own share page show. Nothing here writes to a
// registration: no create, no edit, no delete — those stay on slf-legal.co (see
// server/src/services/leaseReg.ts).
//
// The first addition since the tab was frozen (2026-09-22) is Calem's, report 40865021, opening #1: each row's
// Actions menu offers Liang and Calem "Create Quotation" and "Create Invoice", which open the ordinary
// Quotation / Invoice form with the registration's legal fee, no VAT, and its client — created from the
// registration's name when there is none (POST /api/lease-reg/:id/draft). Since opening #3 the fee comes as the
// firm's service lines, as on Calem's invoice IV-L-20261000001.
//
// The second exception (owner 2026-10-11) is the chevron at the end of each row: it opens a payment-evidence
// row under the registration — the [ LEGAL FEES ] and [ LAND OFFICE FEES/TAXES ] slips the client uploaded on
// the website (mirrored into our bank slips and pinned to a credit by Calem / Nick in the Bank Statement), the
// legal-fee receipt with whether it was sent, and the website's "Confirm Payments" step once both slips are
// confirmed (GET /api/lease-reg/:id/evidence). The 12 columns, the ticks and the Actions menu are as they were;
// nothing here writes to a registration either — the website is written server-side only: on confirm, and (Bugify
// #c3ba0b3e) the legal-fee receipt's PDF put on the client's page once approved and sent; the Receipt tile says when.

// Liang's two logins and Calem's two addresses — the same four the server lets through (server/src/routes/leaseReg.ts
// DRAFTERS). The items also need the Quotation / Invoice module, whose form they open: anyone else is never offered
// a call that would be refused, and a refusal pages the admins.
// …the 'lease_drafter' role, read from /api/me (me.roles) — the server enforces the same role.
type DraftKind = 'quotation' | 'invoice';

type Row = {
  id: number; refId: string; shareCode: string;
  clientName: string | null; developmentNo: string | null; jobId: string | null;
  agentId: number; agent: string; typeLabel: string;
  propertyValue: number; legalFeeTotal: number | null; feesTaxes: number;
  paidCorporate: boolean; paidDedicated: boolean;
  stepNo: number; stepTitle: string; stepTotal: number; isComplete: boolean;
  updatedAt: string; clientUrl: string; developerUrl: string; manageUrl: string;
};

// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below on one 16px line, a 37px band, white where it was light grey.
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '9px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const rt: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };
const ddl: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };
const NAVY = '#0e2a4a';

// "29 Jul 2026" — same rendering as the public back office.
const fmtDate = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString(localeDate(), { timeZone: tz(), day: '2-digit', month: 'short', year: 'numeric' }); };

const PaidTick = ({ title }: { title: string }) => (
  <i className="ti ti-circle-check-filled" title={title} style={{ color: '#15803D', fontSize: 14, marginLeft: 5, verticalAlign: '-2px' }} aria-hidden />
);

// ── Payment evidence row (owner 2026-10-11) ───────────────────────────────────────────────────────
// Same two labels and colours the Jobs page gives the website's two upload slots (Jobs/index.tsx
// STEP_SLOTS 'Payment and Payment Slip'), so a slip reads the same wherever staff meet it.
const SLOT_TILES: { slot: 'corporate' | 'dedicated'; key: string; color: string }[] = [
  { slot: 'corporate', key: 'lr_tile_legal', color: '#2563EB' },
  { slot: 'dedicated', key: 'lr_tile_land', color: '#15803D' },
];
const chip = (bg: string, fg: string, border: string): React.CSSProperties =>
  ({ fontSize: 10.5, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', color: fg, background: bg, border: `1px solid ${border}` });
const CHIP_AMBER = chip('#FEF6E0', '#8A6212', '#F0DCA6');
const CHIP_GREEN = chip('#E6F6EF', '#0F6B49', '#B9E5D2');
const CHIP_GREY = chip('var(--c-gray-100)', 'var(--text-secondary)', 'var(--border)');
const tile: React.CSSProperties = { flex: '1 1 300px', minWidth: 260, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 };
// Epoch seconds, epoch ms or an ISO string — the receipt's sent_at comes from two tables that disagree.
const fmtWhen = (v: number | string | null | undefined) => {
  if (v == null || v === '') return '';
  const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(localeDate(), { timeZone: tz(), day: '2-digit', month: 'short', year: 'numeric' });
};
// The evidence slip as the Bank Statement's thumbnail / lightbox expect it: a client slip (read-only,
// no ×) flagged lease, so the chip reads LEASE REG and the lightbox names the registration.
const asBankSlip = (s: LeaseEvidenceSlip, refLabel: string): BankSlip =>
  ({ id: s.id, filename: s.filename, mime: s.mime, size: s.size, created_at: s.created_at, confirmed_at: s.confirmed_at || 0, confirmed_by_name: s.confirmed_by_name, client: 1, lease: 1, ref_label: refLabel });

function SlipTile({ row, titleKey, color, slips, expected }: {
  row: Row; titleKey: string; color: string; slips: LeaseEvidenceSlip[]; expected: number | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<LeaseEvidenceSlip | null>(null);
  const refLabel = `${row.refId}${row.developmentNo ? ` · ${row.developmentNo}` : ''}`;
  // The bytes come through the Lease Registration route, not the bank one: this tab's users need no bank module.
  const loader = (_scope: string, sid: string) => api.leaseRegSlipObjectUrl(row.id, sid);
  const status = (s: LeaseEvidenceSlip) => {
    if (Number(s.confirmed_at)) return (
      <span style={CHIP_GREEN} title={s.txn?.description || ''}>
        {t('lr_slip_confirmed', { who: s.confirmed_by_name || '—', date: s.txn?.txn_date ? fmtDate(s.txn.txn_date) : '—', amount: s.txn ? moneyTight(s.txn.credit) : '—' })}
      </span>
    );
    if (s.ext_missing === 2) return <a href={row.manageUrl} target="_blank" rel="noopener noreferrer" style={{ ...CHIP_GREY, textDecoration: 'none' }}>{t('lr_slip_on_site')} <i className="ti ti-external-link" style={{ fontSize: 10 }} aria-hidden /></a>;
    if (s.ext_missing === 1) return <span style={CHIP_GREY}>{t('lr_slip_removed')}</span>;
    return <span style={CHIP_AMBER}>{t('lr_slip_tbc')}</span>;
  };
  return (
    <div style={tile}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color }}>{t(titleKey)}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {expected == null ? <i style={{ color: '#B45309' }}>{t('lr_review')}</i> : t('lr_expected', { amount: moneyTight(expected) })}
        </span>
      </div>
      {!slips.length && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('lr_slip_none')}</div>}
      {slips.map(s => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {s.ext_missing
            ? <span style={{ width: 44, height: 44, borderRadius: 6, border: '1px dashed var(--border-strong)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', flexShrink: 0 }}><i className={`ti ti-${s.ext_missing === 2 ? 'world' : 'file-off'}`} style={{ fontSize: 18 }} aria-hidden /></span>
            : <SlipThumb scope="lease" slip={asBankSlip(s, refLabel)} loader={loader} onOpen={() => setOpen(s)} onDelete={() => { /* read-only here */ }} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-all' }}>{s.filename}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('lr_uploaded', { date: fmtWhen(s.created_at) })}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{status(s)}</div>
            {!!s.confirm_note && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{t('lr_slip_note', { note: s.confirm_note })}</div>}
          </div>
        </div>
      ))}
      {/* No txnId → no Confirm button: confirming stays in the Bank Statement, where the credit is. */}
      {open && <SlipLightbox scope="lease" slips={[asBankSlip(open, refLabel)]} loader={loader} onClose={() => setOpen(null)} />}
    </div>
  );
}

function EvidenceRow({ row, colSpan }: { row: Row; colSpan: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<LeaseEvidence | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    api.leaseRegEvidence(row.id)
      .then((d: any) => { if (live) setData(d); })
      .catch((e: any) => { if (live) setErr(e?.message || 'Failed'); });
    return () => { live = false; };
  }, [row.id]);
  const r = data?.receipt || null;
  // Sent beats everything (it went out); then the two gates a receipt waits behind; else it simply has not gone.
  const sentBadge = () => {
    if (!r) return null;
    if (r.sent && 'to' in r.sent) return <span style={CHIP_GREEN}>{t('lr_sent_to', { date: fmtWhen(r.sent.sent_at), to: r.sent.to || '—' })}</span>;
    if (r.sent && 'legacy' in r.sent) return <span style={CHIP_GREEN}>{t('lr_sent_unknown')}</span>;
    if (!r.approval_ok) return <span style={CHIP_AMBER}>{t('lr_pending_approval')}</span>;
    if (Number(r.pending_bank)) return <span style={CHIP_AMBER}>{t('lr_pending_bank')}</span>;
    return <span style={CHIP_GREY}>{t('lr_not_sent')}</span>;
  };
  return (
    <tr>
      <td colSpan={colSpan} style={{ ...td, padding: '10px 14px 14px', background: 'var(--bg-app)' }}>
        {err && <div style={{ fontSize: 12.5, color: '#B42318' }}><i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />{t('lr_evidence_fail')}: {err}</div>}
        {!data && !err && <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 10 }}><i className="ti ti-loader" style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>}
        {data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {SLOT_TILES.map(tl => (
                <SlipTile key={tl.slot} row={row} titleKey={tl.key} color={tl.color} slips={data.slips?.[tl.slot] || []}
                  expected={tl.slot === 'corporate' ? row.legalFeeTotal : row.feesTaxes} />
              ))}
              <div style={tile}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: NAVY }}>{t('lr_tile_receipt')}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('lr_receipt_legal_only')}</span>
                </div>
                {r ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                      <b style={{ color: NAVY }}>{r.doc_no}</b>
                      <span>· {moneyTight(r.amount)}</span>
                      {!!r.payment_date && <span style={{ color: 'var(--text-secondary)' }}>· {fmtDate(r.payment_date)}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {sentBadge()}
                      <button onClick={() => { void api.openReceiptPdf(r.id); }} style={{ ...ddl, padding: '4px 10px', fontSize: 12 }}>
                        <i className="ti ti-file-type-pdf" style={{ fontSize: 12, marginRight: 4, verticalAlign: '-2px' }} aria-hidden />{t('lr_open_pdf')}
                      </button>
                    </div>
                    {/* The server's copy on the client's own page (Bugify #c3ba0b3e), with when it went up. */}
                    {r.client_page && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-cloud-upload" style={{ fontSize: 13, color: '#15803D' }} aria-hidden />
                        {t('lr_receipt_on_page')}{r.client_page.at ? ` · ${fmtDateTime(r.client_page.at)}` : ''}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('lr_receipt_none')}</div>
                    {!data.job && <div style={{ fontSize: 11.5, color: '#B45309' }}><i className="ti ti-info-circle" style={{ fontSize: 12, marginRight: 4, verticalAlign: '-2px' }} aria-hidden />{t('lr_no_job')}</div>}
                  </>
                )}
                {data.job && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('lr_job')}: <b style={{ color: 'var(--text-secondary)' }}>{data.job.job_no}</b></div>}
              </div>
            </div>
            {data.writeback && (
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={`ti ti-${data.writeback.status === 'complete' ? 'circle-check' : 'progress'}`} style={{ fontSize: 13, color: data.writeback.status === 'complete' ? '#15803D' : '#b4894a' }} aria-hidden />
                {t('lr_writeback', { n: data.writeback.stepNo, status: data.writeback.status })}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function ActionsMenu({ row, onCopied, onDraft }: { row: Row; onCopied: (msg: string) => void; onDraft?: (kind: DraftKind) => Promise<void> }) {
  const { t } = useTranslation();
  const site = useFirmContact('legal')?.siteHost || '';   // the public site the registrations live on
  // position:fixed, not absolute — the table lives in a scrolling container, and an absolutely
  // positioned menu gets clipped by it (the last rows would show only a sliver of the menu).
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const open = !!at;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const setOpen = (on: boolean) => {
    if (!on) { setAt(null); return; }
    const r = btnRef.current?.getBoundingClientRect();
    setAt(r ? { x: r.right, y: r.bottom } : { x: 0, y: 0 });
  };
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setAt(null); };
    const close = () => setAt(null);
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', close, true);   // any ancestor scroll → the anchor moved
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  const copy = async (url: string, label: string) => {
    try { await navigator.clipboard.writeText(url); onCopied(label); }
    catch { window.prompt(t('lr_copy_manual'), url); }
    setOpen(false);
  };
  const go = (url: string) => { window.open(url, '_blank', 'noopener'); setOpen(false); };
  const item = (label: string, fn: () => void, icon: string) => (
    <button onClick={fn} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <i className={`ti ti-${icon}`} style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />{label}
    </button>
  );
  const group = (label: string) => (
    <div style={{ padding: '8px 12px 3px', fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)' }}>{label}</div>
  );
  // Create Quotation / Create Invoice: the menu stays open on a spinner while the server reads the registration
  // again and finds or files its client, then closes as the form opens (or a toast says what went wrong).
  const [busy, setBusy] = useState<DraftKind | null>(null);
  const draft = async (kind: DraftKind) => {
    if (busy || !onDraft) return;
    setBusy(kind);
    try { await onDraft(kind); } finally { setBusy(null); setOpen(false); }
  };
  // A registration still on "review" has no legal fee to bill: the item shows greyed, and its tooltip says why.
  const draftItem = (kind: DraftKind, label: string, icon: string) => (row.legalFeeTotal === null
    ? <div title={site ? t('fc_lr_draft_no_fee', { site }) : t('fc_lr_draft_no_fee_plain')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--text-tertiary)', cursor: 'not-allowed' }}>
        <i className={`ti ti-${icon}`} style={{ fontSize: 13 }} aria-hidden />{label}
      </div>
    : item(label, () => { void draft(kind); }, busy === kind ? 'loader' : icon));
  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(!open)} style={{ ...ddl, padding: '5px 10px', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {t('lr_actions')} <i className="ti ti-chevron-down" style={{ fontSize: 11 }} aria-hidden />
      </button>
      {at && (
        <div ref={boxRef} style={{ position: 'fixed', left: Math.max(8, Math.min(at.x - 272, window.innerWidth - 280)), top: Math.min(at.y + 4, window.innerHeight - (onDraft ? 340 : 250)), zIndex: 6000, width: 272, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
          {item(site ? t('fc_lr_open_full', { site }) : t('fc_lr_open_full_plain'), () => go(row.manageUrl), 'external-link')}
          {group(t('lr_client_link'))}
          {item(t('lr_copy_url'), () => copy(row.clientUrl, t('lr_copied_client')), 'copy')}
          {item(t('lr_open_link'), () => go(row.clientUrl), 'arrow-up-right')}
          {group(t('lr_dev_link'))}
          {item(t('lr_copy_url'), () => copy(row.developerUrl, t('lr_copied_dev')), 'copy')}
          {item(t('lr_open_link'), () => go(row.developerUrl), 'arrow-up-right')}
          {onDraft && group(t('lr_draft_group'))}
          {onDraft && draftItem('quotation', t('lr_create_quotation'), 'file-dollar')}
          {onDraft && draftItem('invoice', t('lr_create_invoice'), 'file-invoice')}
        </div>
      )}
    </>
  );
}

export default function LeaseReg({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const site = useFirmContact('legal')?.siteHost || '';   // the public site the registrations live on
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'in_progress' | 'completed'>('in_progress');
  const [agent, setAgent] = useState('all');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState<{ msg: string; n: number } | null>(null);
  // Rows whose payment-evidence row is open; the evidence is fetched when the row opens, never for the whole list.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toastSeq = useRef(0);
  const say = (msg: string) => { toastSeq.current += 1; setToast({ msg, n: toastSeq.current }); };

  const me = useStore(s => s.me);
  const canDraft = (me?.roles || []).includes('lease_drafter')
    && (me?.role === 'admin' || (me?.modules || []).includes('peak'));
  // The open Quotation / Invoice form; `n` remounts it when another row's draft arrives while one is open.
  const [draft, setDraft] = useState<{ n: number; kind: DraftKind; prefill: InvoicePrefill } | null>(null);
  const draftSeq = useRef(0);
  const startDraft = async (r: Row, kind: DraftKind) => {
    try {
      const d: any = await api.leaseRegDraft(r.id);
      draftSeq.current += 1;
      setDraft({ n: draftSeq.current, kind, prefill: { companyId: d.companyId, clientId: d.clientId || '', client: d.client || null, items: d.items } });
    } catch (e: any) { say(e?.message || 'Failed'); }
  };

  const load = () => {
    setLoading(true); setErr('');
    api.leaseRegList()
      .then((d: any) => setRows(d.rows || []))
      .catch((e: any) => setErr(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!toast) return; const h = setTimeout(() => setToast(null), 2200); return () => clearTimeout(h); }, [toast]);

  // Agent options come from the rows themselves (same as the public back office).
  const agents = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rows) if (r.agentId != null) m.set(r.agentId, r.agent || '—');
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const needle = q.trim().toLowerCase();
  // Both filters apply BEFORE the tab split, so the badge counts always match the rows on screen.
  const pool = useMemo(() => rows
    .filter(r => agent === 'all' || String(r.agentId) === agent)
    .filter(r => !needle || `${r.refId} ${r.clientName || ''} ${r.developmentNo || ''} ${r.jobId || ''}`.toLowerCase().includes(needle)),
  [rows, agent, needle]);
  const visible = useMemo(() => pool.filter(r => (tab === 'completed' ? r.isComplete : !r.isComplete)), [pool, tab]);
  const completedCount = pool.filter(r => r.isComplete).length;
  const inProgressCount = pool.length - completedCount;

  const tabBtn = (k: 'in_progress' | 'completed', label: string, n: number) => (
    <button key={k} onClick={() => setTab(k)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 4px', marginRight: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13.5, fontWeight: 700, color: tab === k ? NAVY : 'var(--text-tertiary)', borderBottom: `2px solid ${tab === k ? NAVY : 'transparent'}` }}>
      {label}
      <span style={{ fontSize: 11, fontWeight: 800, minWidth: 20, padding: '1px 7px', borderRadius: 999, background: tab === k ? NAVY : 'var(--c-gray-100)', color: tab === k ? '#fff' : 'var(--text-secondary)' }}>{n}</span>
    </button>
  );

  return (
    <div style={embedded
      ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' }
      : { height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}><img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t('mod_lease_reg')}</div>
        </header>
      )}

      <div style={{ padding: '14px 18px 0', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#b4894a' }}>{t('lr_kicker')}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{t('mod_lease_reg')}</div>
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('lr_search')} style={{ ...ddl, fontWeight: 400, cursor: 'text', width: 210 }} />
          <select value={agent} onChange={e => setAgent(e.target.value)} style={ddl} title={t('lr_agent')}>
            <option value="all">{t('lr_all_agents')}</option>
            {agents.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
          </select>
          <button onClick={load} disabled={loading} title={t('refresh')} style={{ ...ddl, padding: '7px 11px' }}>
            <i className="ti ti-refresh" style={{ fontSize: 14, display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : undefined }} aria-hidden />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {tabBtn('in_progress', t('lr_in_progress'), inProgressCount)}
          {tabBtn('completed', t('lr_completed'), completedCount)}
          <div style={{ flex: 1 }} />
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            <i className="ti ti-lock" style={{ fontSize: 12 }} aria-hidden />{site ? t('fc_lr_readonly', { site }) : t('fc_lr_readonly_plain')}
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px', minHeight: 0 }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : err ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#B42318', background: '#FDE8E8', border: '1px solid #F5B5B1', borderRadius: 8, padding: '10px 13px' }}>
              <i className="ti ti-alert-triangle" aria-hidden />{err}
            </div>
          ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'visible', background: 'var(--bg-surface)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                  <thead><tr>
                    <th style={{ ...th, width: 76 }}>{t('lr_ref')}</th>
                    <th style={th}>{t('lr_client')}</th>
                    <th style={{ ...th, width: 110 }}>{t('lr_agent')}</th>
                    <th style={th}>{t('lr_development')}</th>
                    <th style={{ ...th, width: 90 }}>{t('lr_job')}</th>
                    <th style={{ ...th, width: 108 }}>{t('lr_type')}</th>
                    <th style={{ ...th, minWidth: 168 }}>{t('lr_status')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_lease_value')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_legal_fee')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_fees_taxes')}</th>
                    <th style={{ ...th, width: 96 }}>{t('lr_saved')}</th>
                    {/* 28px chevron column (owner 2026-10-11): opens the payment-evidence row. The 12 columns before it are untouched. */}
                    <th style={{ ...th, width: 28, padding: '10px 4px' }} aria-label={t('lr_evidence_tip')} />
                    <th style={{ ...th, width: 92, borderRight: 'none' }}>{t('lr_actions')}</th>
                  </tr></thead>
                  <tbody>
                    {visible.length === 0 && (
                      <tr><td colSpan={13} style={{ ...td, textAlign: 'center', padding: 28, color: 'var(--text-tertiary)', borderBottom: 'none' }}>
                        {needle || agent !== 'all' ? t('lr_no_match') : t('lr_none')}
                      </td></tr>
                    )}
                    {visible.map(r => (
                      <React.Fragment key={r.id}>
                      <tr>
                        <td style={{ ...td, fontWeight: 800, color: NAVY, whiteSpace: 'nowrap' }}>{r.refId}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.clientName || '—'}</td>
                        <td style={td}><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#EEF2F7', color: NAVY, whiteSpace: 'nowrap' }}>{r.agent}</span></td>
                        <td style={td}>{r.developmentNo || '—'}</td>
                        <td style={td}>{r.jobId
                          ? (/^https?:\/\//i.test(r.jobId)
                            ? <a href={r.jobId} target="_blank" rel="noopener noreferrer" title={r.jobId} style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{r.jobId}</a>
                            : r.jobId)
                          : '—'}</td>
                        <td style={td}>{r.typeLabel}</td>
                        <td style={td}>
                          {r.isComplete
                            ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: '#ECFDF5', color: '#15803D' }}>{t('lr_completed')}</span>
                            : (<>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#b4894a', flexShrink: 0 }} />
                                {t('lr_step_of', { n: r.stepNo, total: r.stepTotal })}
                              </div>
                              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.stepTitle}</div>
                            </>)}
                        </td>
                        <td style={{ ...td, ...rt }}>{fmtNumber(r.propertyValue)} {currencyCode()}</td>
                        <td style={{ ...td, ...rt }}>
                          {r.legalFeeTotal !== null ? <>{fmtNumber(r.legalFeeTotal)} {currencyCode()}{r.paidCorporate && <PaidTick title={t('lr_paid')} />}</>
                            : <span style={{ color: '#B45309', fontStyle: 'italic' }}>{t('lr_review')}</span>}
                        </td>
                        <td style={{ ...td, ...rt }}>{fmtNumber(r.feesTaxes)} {currencyCode()}{r.paidDedicated && <PaidTick title={t('lr_paid')} />}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{fmtDate(r.updatedAt)}</td>
                        <td style={{ ...td, padding: '7px 4px', textAlign: 'center' }}>
                          <button onClick={() => toggle(r.id)} title={t('lr_evidence_tip')} aria-expanded={expanded.has(r.id)}
                            style={{ width: 22, height: 22, padding: 0, border: '1px solid var(--border-strong)', borderRadius: 6, background: expanded.has(r.id) ? NAVY : 'var(--bg-surface)', color: expanded.has(r.id) ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className={`ti ti-chevron-${expanded.has(r.id) ? 'up' : 'down'}`} style={{ fontSize: 12 }} aria-hidden />
                          </button>
                        </td>
                        <td style={{ ...td, borderRight: 'none' }}><ActionsMenu row={r} onCopied={say} onDraft={canDraft ? kind => startDraft(r, kind) : undefined} /></td>
                      </tr>
                      {expanded.has(r.id) && <EvidenceRow row={r} colSpan={13} />}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 6000, background: NAVY, color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '9px 16px', borderRadius: 999, boxShadow: 'var(--shadow-md)' }}>{toast.msg}</div>
      )}
      {/* On document.body, as DocNoHost draws it: outside this console's typeface scope, so the form looks as it does in Peak. */}
      {draft && createPortal(
        <InvoiceForm key={draft.n} invoiceId={null} kind={draft.kind} prefill={draft.prefill}
          onClose={() => setDraft(null)} onSaved={() => say(t(draft.kind === 'quotation' ? 'lr_draft_saved_qo' : 'lr_draft_saved_iv'))} />,
        document.body,
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
